import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { DOCUMENTED_CONSENT_TYPES } from './consent-documents';
import { memberDocumentDownloadUrlDataSchema } from './response';

// ─── member-facing consent document API ─────────────────────────────────────
//
// The wire shapes of the member's own consent-document routes under
// `/api/v1/members/me`. Every response here is client-parsed (mobile and web),
// so the schemas strip unknown keys rather than reject them; only the path
// parameter is strict, and it is strict by being a closed enum.

/** Path parameter: which documented consent type a member-facing document route addresses. */
export const consentDocumentTypeParamSchema = z.enum(DOCUMENTED_CONSENT_TYPES);
export type ConsentDocumentTypeParam = z.infer<typeof consentDocumentTypeParamSchema>;

/**
 * `member_documents` columns served on the member's own document wire object
 * (drift-guarded against the table). Deliberately narrower than the staff
 * projection: the member sees what the document is and when it was signed and
 * filed, never the storage key, hash, byte size or chart id.
 */
export const MEMBER_OWN_DOCUMENT_COLUMN_FIELDS = [
  'id',
  'displayName',
  'signedAt',
  'renderedAt',
  'filedAt',
  'templateVersion',
] as const;

export const memberOwnDocumentSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  signedAt: z.string().datetime({ offset: true }),
  renderedAt: z.string().datetime({ offset: true }),
  filedAt: z.string().datetime({ offset: true }).nullable(),
  templateVersion: z.string(),
});
export type MemberOwnDocument = z.infer<typeof memberOwnDocumentSchema>;

/** The member's standing for a consent type: the highest-version row decides, no row is `none`. */
export const CONSENT_DOCUMENT_STANDING = ['granted', 'declined', 'none'] as const;
export const consentDocumentStandingSchema = z.enum(CONSENT_DOCUMENT_STANDING);
export type ConsentDocumentStanding = z.infer<typeof consentDocumentStandingSchema>;

export const memberConsentDocumentEntrySchema = z.object({
  consentType: consentDocumentTypeParamSchema,
  displayName: z.string(),
  documentVersion: z.string(),
  templateVersion: z.string(),
  /** Whether the registry lets this document render today (`render === 'enabled'`). */
  renderable: z.boolean(),
  standing: consentDocumentStandingSchema,
  version: z.number().int().positive().nullable(),
  recordedAt: z.string().datetime({ offset: true }).nullable(),
  /** The document version the standing row agreed to; null when there is no row. */
  consentDocumentVersion: z.string().nullable(),
  document: memberOwnDocumentSchema.nullable(),
});
export type MemberConsentDocumentEntry = z.infer<typeof memberConsentDocumentEntrySchema>;

export const memberConsentDocumentsDataSchema = z.object({
  documents: z.array(memberConsentDocumentEntrySchema),
});
export type MemberConsentDocumentsData = z.infer<typeof memberConsentDocumentsDataSchema>;
export const memberConsentDocumentsResponseSchema = apiEnvelope(memberConsentDocumentsDataSchema);
export type MemberConsentDocumentsResponse = z.infer<typeof memberConsentDocumentsResponseSchema>;

// `POST /api/v1/members/me/consents/{consentType}/document/preview`: a stamped
// (when the name is known) but unsaved render behind a short-lived presigned
// GET. The URL is a bearer capability — open it, never persist it.
export const consentDocumentPreviewDataSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime({ offset: true }),
  /** True when the signature block carries the member's name and today's date. */
  stamped: z.boolean(),
  consentType: consentDocumentTypeParamSchema,
  displayName: z.string(),
  documentVersion: z.string(),
  templateVersion: z.string(),
});
export type ConsentDocumentPreviewData = z.infer<typeof consentDocumentPreviewDataSchema>;
export const consentDocumentPreviewResponseSchema = apiEnvelope(consentDocumentPreviewDataSchema);
export type ConsentDocumentPreviewResponse = z.infer<typeof consentDocumentPreviewResponseSchema>;

// `POST /api/v1/members/me/consents/{consentType}/document`: the saved document
// (201 when this call rendered it, 200 when it already existed) and a
// presigned download for it.
export const consentDocumentSaveDataSchema = z.object({
  document: memberOwnDocumentSchema,
  downloadUrl: memberDocumentDownloadUrlDataSchema,
});
export type ConsentDocumentSaveData = z.infer<typeof consentDocumentSaveDataSchema>;
export const consentDocumentSaveResponseSchema = apiEnvelope(consentDocumentSaveDataSchema);
export type ConsentDocumentSaveResponse = z.infer<typeof consentDocumentSaveResponseSchema>;
